/**
 * Turns rejected axios requests into a visible message.
 *
 * Without this every `.then()`-only call site fails completely silently: no
 * toast, no reload, nothing but a page that looks wedged. Call sites that
 * render their own inline errors opt out with `skipGlobalErrorHandler: true`
 * on the request config so the user does not get a toast and a field error
 * saying the same thing. A call site that only handles some of the failures
 * itself passes the statuses it handles instead, e.g.
 * `skipGlobalErrorHandler: [404]`, and keeps the toast for everything else.
 */

/**
 * `t()` from SharedTicketFunctions lives in the ticket bundle, and pulling it
 * in here would drag those helpers into every entrypoint. This module only
 * needs a handful of strings, so it carries its own copy.
 *
 * @param {string} en
 * @param {string} sv
 * @returns {string}
 */
const t = function (en, sv) {
  return window.language === 'en' ? en : sv
}

/**
 * Pulls something human readable out of a failed response.
 *
 * A 419, a 502 from the proxy or an nginx 413 all answer with an HTML body, so
 * `data.message` is frequently absent and `data` itself is sometimes a string.
 * Returning null lets the caller fall back to a status-specific message rather
 * than flashing an empty toast.
 *
 * @param {object} error Axios error
 * @returns {?string}
 */
export const serverMessage = function (error) {
  const data = error.response?.data

  if (!data) {
    return null
  }

  // A plain string body is usable when the server meant it as a message, but
  // 419/502/413 answer with a whole HTML document, which is not.
  if (typeof data === 'string') {
    const looksLikeMarkup = data.includes('<')

    return !looksLikeMarkup && data.trim().length > 0 && data.length <= 300
      ? data.trim()
      : null
  }

  // `message` is the Laravel default; `reason` is this application's own
  // convention on several JSON endpoints.
  for (const key of ['message', 'reason']) {
    if (typeof data[key] === 'string' && data[key].length > 0) {
      return data[key]
    }
  }

  const firstError = Object.values(data.errors ?? {})[0]

  if (Array.isArray(firstError)) {
    return firstError[0] ?? null
  }

  return typeof firstError === 'string' ? firstError : null
}

/**
 * How long the session-expired message stays up before we navigate away.
 *
 * A hard redirect the instant the 419 lands would throw away whatever the user
 * had typed before they can even read why.
 */
export const SESSION_EXPIRED_REDIRECT_DELAY = 4000

/**
 * @param {object} error Axios error
 * @returns {boolean} True when a message was shown
 */
export const reportRequestError = function (error) {
  const optOut = error?.config?.skipGlobalErrorHandler
  const status = error?.response?.status

  if (Array.isArray(optOut) ? optOut.includes(status) : optOut) {
    return false
  }

  if (status === 419) {
    window.flash(
      t(
        'Your session has expired. You need to sign in again.',
        'Din session har gått ut. Du behöver logga in igen.',
      ),
      'error',
    )

    window.setTimeout(() => {
      window.location.href = '/login'
    }, SESSION_EXPIRED_REDIRECT_DELAY)

    return true
  }

  if (status === 401) {
    window.flash(
      t(
        'You are not signed in. You need to sign in again.',
        'Du är inte inloggad. Du behöver logga in igen.',
      ),
      'error',
    )

    return true
  }

  if (!error?.response) {
    window.flash(
      t(
        'Could not reach the server. Check your connection and try again.',
        'Kunde inte nå servern. Kontrollera din anslutning och försök igen.',
      ),
      'error',
    )

    return true
  }

  const fallback =
    status >= 500
      ? t(
          'Something went wrong on the server. Please try again.',
          'Något gick fel på servern. Försök igen.',
        )
      : t(
          'The action could not be completed.',
          'Åtgärden kunde inte genomföras.',
        )

  window.flash(serverMessage(error) ?? fallback, 'error')

  return true
}

/**
 * @param {import('axios').AxiosStatic} axios
 */
export const registerHttpErrorHandler = function (axios) {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (!axios.isCancel(error)) {
        reportRequestError(error)
      }

      return Promise.reject(error)
    },
  )
}
